import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useUpdateAtom } from 'jotai/utils';
import cn from 'classnames';
import { stripQueryCriteriaWildcards } from 'src/shared/chat';
import { applyAiSearchIntentAtom } from 'src/shared/dashboardFilters';
import { useAlerts } from 'src/common/AlertManager';
import { useUIActionDispatcher } from './useUIActionDispatcher';
import { useRollbackRunner, buildUnremovableNote } from './useRollbackRunner';
import { getToolLabel, getInputSummary, getResultSummary } from './actionLabels';
import type { ActionCardData, RollbackState, UIAction } from './types';
import styles from './ActionCard.module.scss';

interface ActionCardProps {
  action: ActionCardData;
}

export const ActionCard: React.FC<ActionCardProps> = ({ action }) => {
  const [expanded, setExpanded] = useState(false);
  const [rollbackState, setRollbackState] = useState<RollbackState>('idle');
  const [rollbackNote, setRollbackNote] = useState<string | null>(null);
  const history = useHistory();
  const applyAiSearchIntent = useUpdateAtom(applyAiSearchIntentAtom);
  const { showInfo, showWarning, showAlert } = useAlerts();
  const dispatchUIAction = useUIActionDispatcher();
  const runRollback = useRollbackRunner();

  const label = getToolLabel(action.tool);
  const inputSummary = getInputSummary(action.tool, action.input);
  const resultSummary = action.status === 'success' ? getResultSummary(action.tool, action.result) : '';
  const viewLink = action.uiAction?.route || action.uiAction?.toast?.link;
  const showUndo = action.status === 'success' && !!action.rollback;

  const handleUndo = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!action.rollback || rollbackState !== 'idle') return;

      setRollbackState('in_flight');
      const outcome = await runRollback(action.rollback.opId);

      switch (outcome.kind) {
        case 'reverted': {
          // Either dispatch the server's UIAction verbatim, or if there were
          // partial failures, downgrade the toast to warning before dispatch.
          let actionToDispatch: UIAction | undefined = outcome.uiAction;
          if (outcome.partialFailureCount > 0) {
            const overrideToast = {
              message: `Reverted ${outcome.successCount} of ${outcome.total} assets — ${outcome.partialFailureCount} failed`,
              type: 'warning' as const,
            };
            actionToDispatch = outcome.uiAction
              ? { ...outcome.uiAction, toast: overrideToast }
              : { type: 'toast', toast: overrideToast };
          }
          if (actionToDispatch) dispatchUIAction(actionToDispatch);

          const note = buildUnremovableNote(outcome.unremovableKeys);
          if (note) setRollbackNote(note);

          setRollbackState('reverted');
          break;
        }
        case 'expired':
          if (outcome.reason === 'op_already_consumed') showInfo(outcome.message);
          else showWarning(outcome.message);
          setRollbackState('reverted');
          break;
        case 'auth':
          showAlert(outcome.message);
          setRollbackState('idle');
          break;
        case 'forbidden':
          showAlert(outcome.message);
          setRollbackState('reverted');
          break;
        case 'failed':
          showAlert(outcome.message);
          setRollbackState('idle');
          break;
      }
    },
    [
      action.rollback,
      rollbackState,
      runRollback,
      dispatchUIAction,
      showWarning,
      showInfo,
      showAlert,
    ],
  );

  const handleViewClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (action.queryCriteria && action.tool === 'search_assets') {
        const qc = stripQueryCriteriaWildcards(action.queryCriteria);
        applyAiSearchIntent({
          keyword: qc.query || qc.keyword,
          content_type: qc.content_type,
          file_name: qc.file_name,
          status: qc.status,
          upload_time_range: qc.upload_time_range,
          sort_by: qc.sort_by,
          schema_criteria: qc.schema_criteria,
        });
      }
      if (viewLink) history.push(viewLink);
    },
    [action.queryCriteria, action.tool, viewLink, history, applyAiSearchIntent],
  );

  return (
    <div
      className={cn(styles.card, {
        [styles.pending]: action.status === 'pending',
        [styles.success]: action.status === 'success',
        [styles.error]: action.status === 'error',
        [styles.cancelled]: action.status === 'cancelled',
      })}
    >
      <div className={styles.cardHeader} onClick={() => setExpanded(!expanded)}>
        <div className={styles.statusIcon}>
          {action.status === 'pending' && <div className={styles.spinner} />}
          {action.status === 'success' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="var(--color-green-400)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {action.status === 'error' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 9v4M12 17h.01" stroke="var(--color-red-400)" strokeWidth="2" strokeLinecap="round" />
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="var(--color-red-400)" strokeWidth="1.5" />
            </svg>
          )}
          {action.status === 'cancelled' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect x="6" y="6" width="12" height="12" rx="2" fill="var(--color-gray-400)" />
            </svg>
          )}
        </div>
        <div className={styles.cardInfo}>
          <div className={styles.toolName}>{label}</div>
          {action.status === 'pending' && inputSummary && (
            <div className={styles.summary}>{inputSummary}</div>
          )}
          {action.status === 'success' && (
            <div className={styles.summary}>{resultSummary}</div>
          )}
          {action.status === 'error' && (
            <div className={styles.summary}>Failed</div>
          )}
          {action.status === 'cancelled' && (
            <div className={styles.summary}>Stopped</div>
          )}
        </div>
        <div className={styles.cardActions}>
          {action.status === 'success' && viewLink && (
            <button
              type="button"
              className={styles.viewLink}
              onClick={handleViewClick}
            >
              View
            </button>
          )}
          {showUndo && (
            <button
              type="button"
              className={cn(styles.undoButton, {
                [styles.undoButtonReverted]: rollbackState === 'reverted',
              })}
              onClick={handleUndo}
              disabled={rollbackState !== 'idle'}
              aria-label={rollbackState === 'reverted' ? 'Reverted' : 'Undo this change'}
              title={rollbackState === 'reverted' ? 'Reverted' : 'Undo this change'}
            >
              {rollbackState === 'in_flight' ? (
                <span className={styles.spinnerSm} />
              ) : (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M9 14L4 9l5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 9h11a5 5 0 0 1 0 10h-3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {rollbackState === 'reverted' ? 'Reverted' : 'Undo'}
            </button>
          )}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            className={cn(styles.chevron, { [styles.chevronOpen]: expanded })}
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {rollbackNote && (
        <div className={styles.rollbackNote}>{rollbackNote}</div>
      )}

      {expanded && (
        <div className={styles.cardBody}>
          {action.input && (
            <div className={styles.jsonSection}>
              <div className={styles.jsonLabel}>Input</div>
              <pre className={styles.json}>{JSON.stringify(action.input, null, 2)}</pre>
            </div>
          )}
          {action.result && (
            <div className={styles.jsonSection}>
              <div className={styles.jsonLabel}>Result</div>
              <pre className={styles.json}>{JSON.stringify(action.result, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
